import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, TrainFront, WalletCards, Timer, Share2 } from 'lucide-react';
import { DUBLIN_COMMUTER_MATRIX_2026 } from '../../data/dublinCommuterMatrix';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const DublinCommuterDiscoverPage: React.FC = () => {
  const canonicalUrl = 'https://rentsmart.ie/compare/renting-in-dublin-vs-commuter-towns';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : canonicalUrl;
  const shareText =
    'Compare Dublin vs commuter towns with real rent-vs-commute trade-offs on RentSmart.ie.';

  return (
    <>
      <Helmet>
        <title>Dublin vs Commuter Towns | Rent + Commute Trade-Off Calculator (2026)</title>
        <meta
          name="description"
          content="Compare renting in Dublin vs Naas, Drogheda, Bray, Navan, Maynooth and more. Quantify monthly savings after commute costs."
        />
        <meta
          name="keywords"
          content="renting in dublin vs naas, dublin vs drogheda rent, dublin commuter towns rent comparison, move out of dublin rent"
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden bg-slate-50">
        <header className="bg-slate-900 text-white pt-16 pb-20 px-4 relative overflow-hidden">
          <div className="absolute -top-24 -right-20 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-20 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />

          <div className="relative max-w-6xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-widest mb-6">
              <TrainFront className="h-4 w-4" /> Dublin vs Commuter Trade-Off
            </div>
            <h1 className="text-4xl md:text-7xl font-black tracking-tight mb-4">
              Renting in Dublin vs Commuter Towns
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed max-w-3xl">
              If you are priced out of Dublin, this playbook shows what you actually keep each
              month after commute costs so you can decide fast.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">
                Share:
              </span>
              <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                <FacebookIcon size={30} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareText}>
                <TwitterIcon size={30} round />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl} title={shareText}>
                <WhatsappIcon size={30} round />
              </WhatsappShareButton>
              <LinkedinShareButton
                url={shareUrl}
                title="Dublin vs Commuter Towns"
                summary={shareText}
                source="RentSmart.ie"
              >
                <LinkedinIcon size={30} round />
              </LinkedinShareButton>
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <WalletCards className="h-5 w-5 text-emerald-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Net Savings First</h2>
              <p className="text-sm text-slate-600">
                Monthly gain is rent saving minus commute spend.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <Timer className="h-5 w-5 text-sky-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Time Cost Visible</h2>
              <p className="text-sm text-slate-600">
                One-way commute minutes are shown as part of the decision.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <TrainFront className="h-5 w-5 text-amber-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Transport Facts</h2>
              <p className="text-sm text-slate-600">
                TFI fare cap, TaxSaver context and last service times included.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {DUBLIN_COMMUTER_MATRIX_2026.map((item) => {
              const rentSaving = item.dublinRentAvg - item.townRentAvg;
              const netGain = rentSaving - item.commuteCostMonthly;
              return (
                <Link
                  key={item.slug}
                  to={`/compare/renting-in-dublin-vs-${item.slug}`}
                  className="no-underline group rounded-3xl border border-slate-200 bg-white p-6 hover:-translate-y-1 hover:shadow-xl transition-all"
                >
                  <p className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-2">
                    Dublin vs {item.town}
                  </p>
                  <h2 className="text-2xl font-black text-slate-900 mb-3">{item.town}</h2>
                  <p className="text-sm text-slate-600 mb-4">
                    Net monthly gain after commute: <strong>{formatCurrency(netGain)}</strong>
                  </p>
                  <div className="space-y-1 text-xs font-semibold text-slate-500 mb-4">
                    <p>Dublin rent: {formatCurrency(item.dublinRentAvg)}</p>
                    <p>{item.town} rent: {formatCurrency(item.townRentAvg)}</p>
                    <p>Commute cost: {formatCurrency(item.commuteCostMonthly)}</p>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-black text-emerald-700">
                    Open Comparison{' '}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>

          <section className="mt-10 rounded-3xl border border-amber-200 bg-amber-50 p-6">
            <p className="text-sm text-amber-900 font-semibold leading-relaxed">
              Figures are directional planning estimates for 2026 and do not include every personal
              cost variable. Verify route fares and final lease pricing before signing.
            </p>
          </section>

          <section className="mt-4 rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-emerald-50 p-6">
            <h2 className="text-xl font-black text-slate-900 mb-2 flex items-center gap-2">
              <Share2 className="h-5 w-5 text-emerald-600" /> Share RentSmart.ie
            </h2>
            <p className="text-sm text-slate-600 font-medium mb-4 leading-relaxed">
              Help other renters discover this Dublin commuter trade-off hub on RentSmart.ie.
            </p>
            <div className="flex items-center gap-2">
              <FacebookShareButton url={shareUrl} hashtag="#RentSmartIE">
                <FacebookIcon size={32} round />
              </FacebookShareButton>
              <TwitterShareButton url={shareUrl} title={shareText}>
                <TwitterIcon size={32} round />
              </TwitterShareButton>
              <WhatsappShareButton url={shareUrl} title={shareText}>
                <WhatsappIcon size={32} round />
              </WhatsappShareButton>
              <LinkedinShareButton
                url={shareUrl}
                title="Dublin vs Commuter Towns"
                summary={shareText}
                source="RentSmart.ie"
              >
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
            </div>
          </section>
        </main>
      </div>
    </>
  );
};

export default DublinCommuterDiscoverPage;
